import type React from 'react';
import { useCallback, useState } from 'react';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { centsToDollarsDecimal, dollarsToCents, removeNonDigit } from '@common/paymentsUtils/currencyUtils';
import { MOVE_IN_COSTS_NOTE_MAX_LENGTH } from '@rental-manager/constants/PaymentConstants';
import { getMoveInCostsTotalLimitError, isMoveInCostsNoteLengthValid, validateOtherPageInputs } from '../validation';
import {
  AdornedInput,
  Box,
  Button,
  Divider,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  LabeledControl,
  Legend,
  Radio,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';
import userPersonas from '@common/constants/userPersonas';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  saveStateToReduxStore: (e) => Promise<any>;
  editMode: boolean;
  fromDashboard: boolean;
  otherSettings: Array<{ otherPaymentAmount: number; otherFeeType: string; otherNotes: string }>;
  otherTotalPaymentAmount: number;
  moveInCostsPaymentLimits: number;
  totalPaymentAmount: number;
  showModalNotification: (args: string) => Record<string, any>;
  individualLimit: number;
  otherMicFromLease: Array<Record<string, any>>;
  pageVisited: Set<number>;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClose: () => void;
  togglePrevPage: () => void;
}

const OtherPage: React.FC<Props> = ({
  saveStateToReduxStore,
  editMode = false,
  fromDashboard = false,
  otherSettings: propsOtherSettings,
  otherTotalPaymentAmount: propsOtherTotalPaymentAmount,
  moveInCostsPaymentLimits,
  totalPaymentAmount: propsTotalPaymentAmount,
  individualLimit,
  otherMicFromLease,
  pageVisited,
  currentProperty,
  user,
  onClose,
  togglePrevPage,
}) => {
  const getEmptyError = () => ({
    otherFeeType: '',
    otherPaymentAmount: '',
    otherNotes: '',
  });

  const otherSettingsCentsToDollars = propsOtherSettings.map((setting) => ({
    ...setting,
    otherPaymentAmount: centsToDollarsDecimal(setting.otherPaymentAmount),
  }));

  const initialIsOpen =
    !editMode && !pageVisited?.has(3) && propsOtherSettings.length === 0 && otherMicFromLease?.length > 0
      ? true
      : Boolean(propsOtherSettings.length);

  const initialOtherSettings = [...otherSettingsCentsToDollars];
  const initialErrorsList = propsOtherSettings.map(() => getEmptyError());

  // Add lease data if conditions are met
  if (!editMode && !pageVisited?.has(3) && propsOtherSettings.length === 0 && otherMicFromLease?.length > 0) {
    otherMicFromLease.forEach((otherMic) => {
      initialOtherSettings.push({
        otherFeeType: otherMic.type,
        otherPaymentAmount: centsToDollarsDecimal(otherMic.value),
        otherNotes: '',
      });
      initialErrorsList.push(getEmptyError());
    });
  }

  const hasAnsweredBefore = editMode || Boolean(pageVisited?.has(3));
  const [isOpen, setIsOpen] = useState<boolean | null>(initialIsOpen ? true : hasAnsweredBefore ? false : null);
  const [otherSettings, setOtherSettings] = useState(initialOtherSettings);
  const [errorsList, setErrorsList] = useState(initialErrorsList);

  const addAnotherMoveInCost = useCallback((event = { preventDefault: () => {} }): void => {
    event.preventDefault();
    setOtherSettings((prev) => [...prev, { otherFeeType: '', otherPaymentAmount: '', otherNotes: '' }]);
    setErrorsList((prev) => [...prev, getEmptyError()]);
  }, []);

  const toggleOpen = useCallback((): void => {
    setIsOpen(true);
    if (otherSettings.length === 0) {
      addAnotherMoveInCost();
    }
  }, [otherSettings.length, addAnotherMoveInCost]);

  const skipOtherPage = useCallback(async (): Promise<any> => {
    setIsOpen(false);

    IreneOnboardingCSEvents.emitSkipOtherMICEvent({ currentProperty, user });

    const payload = {
      otherSettings: [],
    };

    await saveStateToReduxStore(payload);
  }, [currentProperty, user, saveStateToReduxStore]);

  const handleNotesChange = useCallback((e): void => {
    const key = e.target.dataset.statekey;
    const id = e.target.dataset.id;
    const value = e.target.value;

    setOtherSettings((prev) => {
      const newSettings = [...prev];
      newSettings[id][key] = value;
      return newSettings;
    });

    setErrorsList((prev) => {
      const newErrors = [...prev];
      newErrors[id][key] = '';
      return newErrors;
    });
  }, []);

  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleAmountChange = useCallback((e): void => {
    e.preventDefault();
    const key = e.target.dataset.statekey;
    const id = e.target.dataset.id;
    const value = e.target.value;
    const error = '';
    const otherPaymentAmount = removeNonDigit(value);

    setOtherSettings((prev) => {
      const newSettings = [...prev];
      newSettings[id][key] = otherPaymentAmount;
      return newSettings;
    });

    setErrorsList((prev) => {
      const newErrors = [...prev];
      newErrors[id][key] = error;
      return newErrors;
    });
  }, []);

  const deleteThisMoveInCost = useCallback((idx): void => {
    setOtherSettings((prev) => {
      const newSettings = [...prev];
      newSettings.splice(idx, 1);
      return newSettings;
    });

    setErrorsList((prev) => {
      const newErrors = [...prev];
      newErrors.splice(idx, 1);
      return newErrors;
    });
  }, []);

  const handleSaveStateToReduxStore = async (e): Promise<any> => {
    e.preventDefault();

    const validInputs = validateOtherPageInputs({
      otherSettings,
      errorsList,
      individualLimit,
      handleErrors: (errors) => {
        if (errors.errorsList) setErrorsList([...errors.errorsList]);
      },
    });

    if (!validInputs) {
      return;
    }

    let currentOtherTotal = 0;
    otherSettings.forEach((setting) => {
      currentOtherTotal += parseInt(dollarsToCents(String(setting.otherPaymentAmount)), 10);
    });

    const totalPaymentAmount =
      propsOtherSettings.length === 0
        ? propsTotalPaymentAmount
        : propsTotalPaymentAmount - propsOtherTotalPaymentAmount;

    if (totalPaymentAmount + currentOtherTotal > moveInCostsPaymentLimits) {
      const limitError = getMoveInCostsTotalLimitError(moveInCostsPaymentLimits);
      setErrorsList((prev) => prev.map((errors) => ({ ...errors, otherPaymentAmount: limitError })));
      return;
    }

    IreneOnboardingCSEvents.emitContinueFromOtherMICEvent({
      currentProperty,
      user,
    });

    const payload = {
      otherSettings: otherSettings.map((setting) => ({
        ...setting,
        otherPaymentAmount: parseInt(dollarsToCents(String(setting.otherPaymentAmount)), 10),
      })),
    };

    await saveStateToReduxStore(payload);
  };

  const renderOpenedSection = (): JSX.Element => {
    return (
      <>
        {otherSettings.map((val, idx) => {
          return (
            <Box key={idx}>
              <Divider css={{ marginBlock: 'layout.tight' }} />
              <Box
                css={{
                  display: 'grid',
                  gridTemplateRows: 'auto',
                  gap: 'default',
                }}
              >
                <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Heading level={3} textStyle="body-lg-bold">
                    {`Move-in cost #${idx + 1}`}
                  </Heading>
                  <TextButton onClick={() => deleteThisMoveInCost(idx)}>Delete</TextButton>
                </Box>

                <FormField
                  css={{ maxWidth: '36rem' }}
                  error={
                    Boolean(errorsList[idx].otherFeeType) ||
                    !isMoveInCostsNoteLengthValid(otherSettings[idx].otherFeeType)
                  }
                  required
                  control={
                    <Input
                      data-statekey="otherFeeType"
                      data-id={idx}
                      className="fs-exclude"
                      value={otherSettings[idx].otherFeeType}
                      onChange={handleNotesChange}
                      onFocus={() => setFocusedField(`otherFeeType-${idx}`)}
                      onBlur={() => setFocusedField(null)}
                    />
                  }
                  description={
                    errorsList[idx].otherFeeType ? (
                      <FormHelp>{errorsList[idx].otherFeeType}</FormHelp>
                    ) : (
                      <CharacterCountFormHelp
                        currentLength={otherSettings[idx].otherFeeType.length}
                        maxLength={MOVE_IN_COSTS_NOTE_MAX_LENGTH}
                        isFocused={focusedField === `otherFeeType-${idx}`}
                        errorText={`This must be ${MOVE_IN_COSTS_NOTE_MAX_LENGTH} characters or fewer`}
                      />
                    )
                  }
                  label={<Label>For</Label>}
                />

                <FormField
                  css={{ maxWidth: '36rem' }}
                  error={Boolean(errorsList[idx].otherPaymentAmount)}
                  required
                  control={
                    <AdornedInput
                      fluid
                      input={
                        <AdornedInput.Input
                          min="0"
                          type="number"
                          className="fs-exclude input-caret-hide"
                          value={!otherSettings[idx].otherPaymentAmount ? '' : otherSettings[idx].otherPaymentAmount}
                          onChange={handleAmountChange}
                          onBlur={handleAmountChange}
                          onFocus={handleAmountChange}
                          data-statekey="otherPaymentAmount"
                          data-id={idx}
                        />
                      }
                      size="md"
                      startAdornment={<AdornedInput.Adornment id="start-adornment">$</AdornedInput.Adornment>}
                    />
                  }
                  description={
                    errorsList[idx].otherPaymentAmount && <FormHelp>{errorsList[idx].otherPaymentAmount}</FormHelp>
                  }
                  label={<Label>Amount</Label>}
                />

                <FormField
                  css={{ maxWidth: '36rem' }}
                  error={!isMoveInCostsNoteLengthValid(otherSettings[idx].otherNotes)}
                  optional
                  control={
                    <Textarea
                      data-statekey="otherNotes"
                      data-id={idx}
                      className="fs-exclude"
                      css={{ resize: 'vertical' }}
                      value={otherSettings[idx].otherNotes}
                      onChange={handleNotesChange}
                      onFocus={() => setFocusedField(`otherNotes-${idx}`)}
                      onBlur={() => setFocusedField(null)}
                    />
                  }
                  description={
                    <CharacterCountFormHelp
                      currentLength={otherSettings[idx].otherNotes.length}
                      maxLength={MOVE_IN_COSTS_NOTE_MAX_LENGTH}
                      isFocused={focusedField === `otherNotes-${idx}`}
                      label="Note for tenant"
                    />
                  }
                  label={<Label>Note for tenant</Label>}
                />
              </Box>
            </Box>
          );
        })}
        <TextButton css={{ marginTop: 'layout.tight', justifySelf: 'start' }} onClick={addAnotherMoveInCost}>
          Add another move-in cost
        </TextButton>
      </>
    );
  };

  const renderMainSection = (): JSX.Element => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'default',
        }}
      >
        <FieldSet required legend={<Legend size="lg">Would you like to collect any other move-in costs?</Legend>}>
          <LabeledControl
            control={<Radio name="other-move-in-costs" value="yes" checked={isOpen === true} onChange={toggleOpen} />}
            label={<Label>Yes</Label>}
          />
          <LabeledControl
            control={
              <Radio
                name="other-move-in-costs"
                value="no"
                checked={isOpen === false}
                onChange={(): void => setIsOpen(false)}
              />
            }
            label={<Label>No</Label>}
          />
        </FieldSet>
        {isOpen && renderOpenedSection()}
      </Box>
    );
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      aside={<FaqComponent faqs={FaqConstants.ONBOARDING_V2.MOVE_IN_COSTS.OTHER_PAGE} />}
      onRequestClose={onClose}
      header={fromDashboard ? 'Move-in costs' : 'Payment setup'}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          {renderMainSection()}
        </Box>
      }
      backAction={{ onClick: togglePrevPage }}
      footerAriaLabel="Other move-in costs actions"
      footerEnd={
        <Button
          emphasis="filled"
          disabled={isOpen === null}
          onClick={isOpen ? handleSaveStateToReduxStore : skipOtherPage}
        >
          Continue
        </Button>
      }
    />
  );
};

export default OtherPage;
